import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import * as Sentry from '@sentry/react';

import i18n from '../../i18n';

import Api from '../../api/Api';
import FakeApi from '../../api/FakeApi';

import Exchange from '../../models/Exchange';
import Conversation from '../../models/Conversation';
import Availability from '../../models/Availability';
import User from '../../models/User';

import HomeSwitcher from './HomeSwitcher';
import EditableField from '../widget/EditableField';
import EditableDate from '../widget/EditableDate';
import EditableExchangeType from './EditableExchangeType';
import SubscriptionRow from './SubscriptionRow';
import GuestpointsField from './GuestpointsField';
import OverlayTransformToMutual from './OverlayTransformToMutual';
import CalendarUtils from '../../utils/CalendarUtils';
import Tour from '../../utils/Tour';
import ReciprocalModal from './ReciprocalModal';
import {
    fetchStayRequestForConversation,
    updateDatesForConversation,
    updateStayRequestToNonReciprocal,
    updateStayRequestToReciprocal
} from '../../actions/stayRequest';

class ExchangeEditable extends React.Component {
    static propTypes = {
        currency: PropTypes.string.isRequired,
        user: PropTypes.instanceOf(User).isRequired,
        conversation: PropTypes.instanceOf(Conversation).isRequired,
        exchange: PropTypes.instanceOf(Exchange).isRequired,
        patchExchange: PropTypes.func.isRequired,
        coupon: PropTypes.shape({
            couponName: PropTypes.string,
            discount: PropTypes.number
        }),
        actions: PropTypes.shape({
            updateDatesForConversation: PropTypes.func.isRequired,
            fetchStayRequestForConversation: PropTypes.func.isRequired,
            updateStayRequestToReciprocal: PropTypes.func.isRequired,
            updateStayRequestToNonReciprocal: PropTypes.func.isRequired
        }).isRequired
    };

    constructor(props, context) {
        super(props, context);
        this.state = {
            availabilities: []
        };
        this.resumeRef = React.createRef();
        this.startOnFieldRef = React.createRef();
        this.endOnFieldRef = React.createRef();
        this.changeToReciprocal = this.changeToReciprocal.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleHomeChange = this.handleHomeChange.bind(this);
        this.adjustDeposit = this.adjustDeposit.bind(this);
        this.adjustInsurance = this.adjustInsurance.bind(this);
        this.isGuest = this.isGuest.bind(this);
        this.rangeValidator = this.rangeValidator.bind(this);
        this.guestNbRenderer = this.guestNbRenderer.bind(this);
        this.dateRenderer = this.dateRenderer.bind(this);
        this.startOnValidator = this.startOnValidator.bind(this);
        this.endOnValidator = this.endOnValidator.bind(this);
        this.handleAvailabilityCallback = this.handleAvailabilityCallback.bind(this);
        this.renderServicePlusSection = this.renderServicePlusSection.bind(this);
        this.guestNbValidator = this.guestNbValidator.bind(this);
        this.handleStayRequestDateChange = this.handleStayRequestDateChange.bind(this);
    }

    componentDidMount() {
        // fetch home availabilities
        this.fetchAvailabilitiesForHome(this.props.exchange.get('home'));
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // fetch home availabilities if home changed
        if (
            nextProps.exchange.get('home') &&
            (!this.props.exchange.get('home') ||
                nextProps.exchange.get('home').id != this.props.exchange.get('home').id)
        ) {
            this.fetchAvailabilitiesForHome(nextProps.exchange.get('home'));
        }
    }

    componentWillUnmount() {
        this._isUnmounted = true;
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            !_.isEqual(nextProps.conversation, this.props.conversation) ||
            !_.isEqual(nextProps.exchange, this.props.exchange) ||
            !_.isEqual(nextState.availabilities, this.state.availabilities) ||
            !_.isEqual(this.props.coupon, nextProps.coupon)
        );
    }

    getOtherExchange() {
        if (this.props.conversation.isMutual()) {
            if (this.isGuest()) {
                return this.props.conversation.getExchangeAsHost();
            } else {
                return this.props.conversation.getExchangeAsGuest();
            }
        }
    }

    isGuest() {
        const guest = this.props.exchange.get('guest');
        return guest && guest.id == this.props.user.id;
    }

    fetchAvailabilitiesForHome(home) {
        if (!home || !home.id) {
            return;
        }
        return Tour.shouldUseFakeApi('messaging_tour', 'Home.getAvailabilities').then((shouldUseFakeApi) => {
            if (shouldUseFakeApi) {
                FakeApi.MessagingTour.Home.getAvailabilities(home.id).done(this.handleAvailabilityCallback);
            } else {
                Api.Home.getAvailabilities(home.id).done(this.handleAvailabilityCallback);
            }
        });
    }

    handleAvailabilityCallback(result) {
        if (result && result.data) {
            // Create Availability objects
            const availabilities = result.data.map((availability) => new Availability(availability));
            if (this._isUnmounted) {
                // component is unmounted, don't update it
                return;
            }
            this.setState({
                availabilities
            });
        }
    }

    rangeValidator(start, end) {
        if (!start || !end) {
            return true;
        }
        let isValid = true;
        this.state.availabilities.forEach((availability) => {
            if (availability.get('type') === Availability.BOOKED.type) {
                const dispoRange = moment.range(availability.get('start_on'), availability.get('end_on'));
                const selectedRange = moment.range(start, end);
                if (selectedRange.overlaps(dispoRange)) {
                    isValid = false;
                }
            }
        });
        return isValid;
    }

    handleChange(field, value) {
        if (!this.props.exchange) {
            throw Error('No exchange selected');
        }
        if (
            (_.isObject(value) && _.isEqual(this.props.exchange.get(field), value)) ||
            this.props.exchange.get(field) == value
        ) {
            return false; // not changed
        }

        const fields = {};
        fields[field] = value;

        // answer user confirmation if needed and update exchange
        this.updateIfConfirm((isConfirm) => {
            if (isConfirm) {
                this.updateField(fields);

                this.props.patchExchange(this.props.exchange.id, fields);
            } else {
                // force update to reset value
                this.forceUpdate();
            }
        });
    }

    updateIfConfirm(cb) {
        if (!this.props.exchange) {
            throw Error('No exchange selected');
        }
        // alert user for modification
        if (this.props.exchange.get('status') > Exchange.STATUS_DEFAULT) {
            sweetAlert({
                title: i18n.t('exchange:confirm_update_title'),
                text: i18n.t('exchange:confirm_update_text'),
                icon: 'warning',
                buttons: [i18n.t('common:cancel'), i18n.t('common:continue')]
            }).then((isConfirm) => {
                cb(isConfirm);
            });
        } else {
            cb(true);
        }
    }

    updateField(fields) {
        for (const field in fields) {
            this.props.exchange.set(field, fields[field]);
        }

        const header = document.querySelector('.exchange-header');
        if (header) {
            header.dispatchEvent(new CustomEvent('loading'));
        }
    }

    handleStayRequestDateChange(field, value) {
        if (!this.props.exchange) {
            throw Error('No exchange selected');
        }
        if (
            (_.isObject(value) && _.isEqual(this.props.exchange.get(field), value)) ||
            this.props.exchange.get(field) == value
        ) {
            return false; // not changed
        }

        const fields = {};
        fields[field] = value;
        // update end_on if set start_on after current end_on
        if (
            field === 'start_on' &&
            this.props.exchange.has('end_on') &&
            moment(value).isSameOrAfter(this.props.exchange.get('end_on'))
        ) {
            fields.end_on = moment(value).add(1, 'days');
            this.props.exchange.set('end_on', fields.end_on);
        }
        if (field === 'start_on' && this.props.exchange.has('end_on')) {
            const range = moment.range(value, this.props.exchange.get('end_on'));
            if (CalendarUtils.overlapsBookedPeriod(range, this.state.availabilities)) {
                fields.end_on = moment(value).add(1, 'days');
                this.props.exchange.set('end_on', fields.end_on);
            }
        }

        if (field === 'start_on' && !fields.end_on) {
            fields.end_on = moment(this.props.exchange.get('end_on'));
        }
        if (field === 'end_on' && !fields.start_on) {
            fields.start_on = moment(this.props.exchange.get('start_on'));
        }

        // answer user confirmation if needed and update exchange
        this.updateIfConfirm(async (isConfirm) => {
            if (isConfirm) {
                const { start_on, end_on } = fields;

                const getDate = (date) => {
                    if (!date?.isValid()) {
                        return null;
                    }
                    return moment(date).format('YYYY-MM-DD');
                };

                const startOn = getDate(start_on);
                const endOn = getDate(end_on);

                this.updateField(fields);
                // focus end_on picker if update start_on and end_on is not defined
                if (_.has(fields, 'start_on') && !this.props.exchange.has('end_on')) {
                    this.endOnFieldRef.current.edit();
                }

                try {
                    await this.props.actions.updateDatesForConversation(
                        this.props.conversation.get('id'),
                        this.props.exchange.id,
                        startOn,
                        endOn
                    );

                    this.props.actions.fetchStayRequestForConversation(this.props.conversation);
                } catch (error) {
                    Sentry.captureException(error, {
                        tags: { feature: 'update-stay-request-dates' },
                        extra: {
                            body: {
                                field,
                                conversationId: this.props.conversation.get('id'),
                                exchangeId: this.props.exchange.id,
                                startOn,
                                endOn
                            }
                        }
                    });
                }
            } else {
                // force update to reset value
                this.forceUpdate();
            }
        });
    }

    changeToReciprocal(home) {
        this.updateIfConfirm(() => {
            this.props.actions.updateStayRequestToReciprocal(this.props.conversation.id, home.id);
            const header = document.querySelector('.exchange-header');
            if (header) {
                header.dispatchEvent(new CustomEvent('loading'));
            }
        });
    }

    handleHomeChange(home) {
        this.handleChange('home', home);
    }

    adjustDeposit(event) {
        event.preventDefault();
        const deposit = this.getOtherExchange().get('home_deposit');
        this.handleChange('home_deposit', deposit);
        $(event.target).closest('.alert-dismissible').removeClass('in');
    }

    adjustInsurance(event) {
        event.preventDefault();
        const insurance = this.getOtherExchange().get('insurance');
        this.handleChange('insurance', insurance);
        $(event.target).closest('.alert-dismissible').removeClass('in');
    }

    openOpionsPanel(event) {
        event.preventDefault();
        document.dispatchEvent(
            new CustomEvent('request_card', {
                detail: {
                    view: 'options'
                }
            })
        );
    }

    startOnValidator(date) {
        return this.rangeValidator(date, this.props.exchange.get('end_on'));
    }

    endOnValidator(date) {
        return this.rangeValidator(this.props.exchange.get('start_on'), date);
    }

    renderServicePlusSection() {
        const { currency, exchange, user } = this.props;

        const subscription = user.get('subscription');
        const isTrial = subscription?.is_trial;

        if (exchange.hasSelectedSubscription() && this.isGuest() && (!user.isSubscriber() || isTrial)) {
            return (
                <React.Fragment>
                    <SubscriptionRow
                        key="subscription"
                        currency={currency}
                        exchange={exchange}
                        isGuest={this.isGuest()}
                        isSubscriber={user.isSubscriber()}
                        user={user}
                    />
                </React.Fragment>
            );
        }
    }

    render() {
        const capacity = this.props.exchange.get('home') && this.props.exchange.get('home').get('capacity');
        return (
            <div
                ref={this.resumeRef}
                className={classNames('exchange-resume', {
                    preview: !this.props.exchange.id
                })}
                data-exchange={this.props.exchange.id}
            >
                <HomeSwitcher
                    exchange={this.props.exchange}
                    switchable={true}
                    onChange={this.handleHomeChange}
                    isOnModification={this.props.conversation.isInExchangeModificationFlowAndShouldBuyGp()}
                />

                <div className="exchange-fields">
                    <div className="form-group-inline dates-travelers">
                        <EditableField
                            editable={
                                this.isGuest() &&
                                !this.props.conversation.isInExchangeModificationFlowAndShouldBuyGp()
                            }
                            name="guest_nb"
                            label={i18n.t('common:guests')}
                            value={this.props.exchange.get('guest_nb')}
                            renderer={this.guestNbRenderer}
                            onChange={this.handleChange}
                            validator={this.guestNbValidator}
                            exchangeIsApproved={this.props.exchange.isApproved()}
                        >
                            <input
                                type="number"
                                pattern="\d*"
                                className="form-control"
                                id="guest_nb"
                                name="guest_nb"
                                min={this.props.exchange.isApproved() ? '0' : '1'}
                                max={capacity}
                                placeholder={capacity && `${capacity} max.`}
                            />
                        </EditableField>
                        <EditableDate
                            editable={!this.props.conversation.isInExchangeModificationFlowAndShouldBuyGp()}
                            name="start_on"
                            label={i18n.t('exchange:start_on')}
                            value={this.props.exchange.get('start_on')}
                            startOn={this.props.exchange.get('start_on')}
                            endOn={this.props.exchange.get('end_on')}
                            renderer={this.dateRenderer}
                            onChange={this.handleStayRequestDateChange}
                            validator={this.startOnValidator}
                            ref={this.startOnFieldRef}
                            inputType="start"
                            availabilities={this.state.availabilities}
                        />
                        <EditableDate
                            editable={!this.props.conversation.isInExchangeModificationFlowAndShouldBuyGp()}
                            name="end_on"
                            label={i18n.t('exchange:end_on')}
                            value={this.props.exchange.get('end_on')}
                            startOn={this.props.exchange.get('start_on')}
                            endOn={this.props.exchange.get('end_on')}
                            renderer={this.dateRenderer}
                            onChange={this.handleStayRequestDateChange}
                            validator={this.endOnValidator}
                            ref={this.endOnFieldRef}
                            inputType="end"
                            availabilities={this.state.availabilities}
                        />
                    </div>

                    <EditableExchangeType
                        conversation={this.props.conversation}
                        user={this.props.user}
                        deleteReciprocal={this.props.actions.updateStayRequestToNonReciprocal}
                    />

                    <GuestpointsField
                        conversation={this.props.conversation}
                        exchange={this.props.exchange}
                        isGuest={this.isGuest()}
                        isOnModification={this.props.conversation.isInExchangeModificationFlowAndShouldBuyGp()}
                        handleChange={this.handleChange}
                        currency={this.props.currency}
                        coupon={this.props.coupon}
                    />

                    {this.renderServicePlusSection()}

                    {/* For a non-reciprocal exchange, when you click on the second tab, you have a button (with an overlay) to change the exchange to reciprocal */}
                    {!this.props.conversation.isInExchangeModificationFlowAndShouldBuyGp() && (
                        <OverlayTransformToMutual
                            exchange={this.props.exchange}
                            user={this.props.user}
                            changeToReciprocal={this.changeToReciprocal}
                        />
                    )}
                </div>
                {!this.isGuest() && <ReciprocalModal conversation={this.props.conversation} />}
            </div>
        );
    }

    guestNbRenderer(value) {
        // Treat LHS data merge case here
        if (this.props.exchange.isApproved() && value === 0) {
            return this.isGuest() ? '?' : value;
        }
        return value ? value : this.isGuest() ? '?' : '--';
    }

    guestNbValidator(value) {
        if (this.props.exchange.isApproved() && value === 0) {
            return value;
        }
        return value >= 1;
    }

    dateRenderer(value) {
        if (value) {
            return [moment(value).format('DD'), moment(value).format('MMM'), moment(value).format('YY')]
                .map((part) => `<span>${part}</span>`)
                .join(' ');
        }
        return `<span>${this.isGuest() ? '?' : '--'}</span>`;
    }
}

const mapStateToProps = () => {
    return {};
};

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(
            {
                updateDatesForConversation,
                fetchStayRequestForConversation,
                updateStayRequestToReciprocal,
                updateStayRequestToNonReciprocal
            },
            dispatch
        )
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ExchangeEditable);
